import React, { useCallback } from 'react'
import { Form, Select, Input, Button, Typography, Spin, Row } from 'antd'
import { map, filter, isEmpty, flatten } from 'lodash-es'
import { useQuery } from '@apollo/client'
import { SCRAPING_TEST_CASE_COUNTS_WITH_TAGS } from '../../graphql/queries'
import {
  CUSTOM,
  MENU_TYPE,
  MENU_STRUCTURE,
  MENU,
  MENU_LIST,
  SECTION_LIST,
  DISH_LIST,
  GLOBAL,
  SCRAPING_FLAG,
  SCRAPING_TAG_TYPES,
} from '../../constants'
import { scrapingTagGroupEditorFormInitialValues } from './initial-values'
import PropTypes from 'prop-types'

const layout = {
  labelCol: { span: 9 },
  wrapperCol: { span: 15 },
}

const tailLayout = {
  wrapperCol: { offset: 20, span: 2 },
}

const ScrapingTagGroupEditorForm = ({
  scrapingTagGroup,
  tags,
  onSubmit,
  isSubmitting,
}) => {
  const {
    data: { scrapingTestCaseCountsWithTagIds } = {},
    loading: isLoadingScrapingTestCaseCounts,
    refetch: refetchScrapingTestCaseCounts,
  } = useQuery(SCRAPING_TEST_CASE_COUNTS_WITH_TAGS, {
    variables: { tagIds: map(scrapingTagGroup?.tags, 'id') },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
  })

  const initialValues = scrapingTagGroupEditorFormInitialValues(
    scrapingTagGroup,
  )

  const customOptions = map(
    filter(tags, ({ type }) => type === CUSTOM),
    tag => ({ label: tag.name, value: tag.id }),
  )

  const menuStructureOptions = map(
    filter(tags, ({ type }) => type === MENU_STRUCTURE),
    tag => ({ label: tag.name, value: tag.id }),
  )

  const menuTypeOptions = map(
    filter(tags, ({ type }) => type === MENU_TYPE),
    tag => ({ label: tag.name, value: tag.id }),
  )

  const menuOptions = map(
    filter(tags, ({ type }) => type === MENU),
    tag => ({ label: tag.name, value: tag.id }),
  )

  const menuListOptions = map(
    filter(tags, ({ type }) => type === MENU_LIST),
    tag => ({ label: tag.name, value: tag.id }),
  )

  const sectionListOptions = map(
    filter(tags, ({ type }) => type === SECTION_LIST),
    tag => ({ label: tag.name, value: tag.id }),
  )

  const dishListOptions = map(
    filter(tags, ({ type }) => type === DISH_LIST),
    tag => ({ label: tag.name, value: tag.id }),
  )

  const globalOptions = map(
    filter(tags, ({ type }) => type === GLOBAL),
    tag => ({ label: tag.name, value: tag.id }),
  )

  const scrapingFlagOptions = map(
    filter(tags, ({ type }) => type === SCRAPING_FLAG),
    tag => ({ label: tag.name, value: tag.id }),
  )

  const onChange = useCallback(
    async (formValue, formValues) => {
      if (
        formValue[0]?.name[0] === 'alias' ||
        formValue[0]?.name[0] === 'description'
      )
        return

      const tagFields = filter(
        formValues,
        formValue =>
          !isEmpty(formValue.value) &&
          formValue.name[0] !== 'alias' &&
          formValue.name[0] !== 'description' &&
          formValue.name[0] !== 'id',
      )

      const tagIds = flatten(map(tagFields, ({ value }) => value))

      await refetchScrapingTestCaseCounts({ tagIds })
    },
    [refetchScrapingTestCaseCounts],
  )

  return (
    <Form
      initialValues={initialValues}
      onFinish={onSubmit}
      preserve={false}
      scrollToFirstError
      onFieldsChange={onChange}
      {...layout}
    >
      <Form.Item name="id" style={{ display: 'none' }}>
        <Input />
      </Form.Item>
      <Form.Item label="Tag group alias" name="alias">
        <Input />
      </Form.Item>
      <Form.Item label="Tag group description" name="description">
        <Input />
      </Form.Item>
      <Form.Item label={SCRAPING_TAG_TYPES[CUSTOM].name} name="customTagIds">
        <Select options={customOptions} mode="multiple" />
      </Form.Item>

      <Form.Item
        label={SCRAPING_TAG_TYPES[MENU_STRUCTURE].name}
        name="menuStructureTagId"
      >
        <Select options={menuStructureOptions} />
      </Form.Item>

      <Form.Item
        label={SCRAPING_TAG_TYPES[MENU_TYPE].name}
        name="menuTypeTagId"
      >
        <Select options={menuTypeOptions} />
      </Form.Item>

      <Form.Item label={SCRAPING_TAG_TYPES[MENU].name} name="menuTagIds">
        <Select options={menuOptions} mode="multiple" />
      </Form.Item>

      <Form.Item
        label={SCRAPING_TAG_TYPES[MENU_LIST].name}
        name="menuListTagIds"
      >
        <Select options={menuListOptions} mode="multiple" />
      </Form.Item>

      <Form.Item
        label={SCRAPING_TAG_TYPES[SECTION_LIST].name}
        name="sectionListTagIds"
      >
        <Select options={sectionListOptions} mode="multiple" />
      </Form.Item>

      <Form.Item
        label={SCRAPING_TAG_TYPES[DISH_LIST].name}
        name="dishListTagIds"
      >
        <Select options={dishListOptions} mode="multiple" />
      </Form.Item>

      <Form.Item label={SCRAPING_TAG_TYPES[GLOBAL].name} name="globalTagIds">
        <Select options={globalOptions} mode="multiple" />
      </Form.Item>

      <Form.Item
        label={SCRAPING_TAG_TYPES[SCRAPING_FLAG].name}
        name="scrapingFlagTagIds"
      >
        <Select options={scrapingFlagOptions} mode="multiple" />
      </Form.Item>
      <Row justify="end">
        {isLoadingScrapingTestCaseCounts ? (
          <Spin size="small" />
        ) : (
          <Typography.Text
            strong
          >{`${scrapingTestCaseCountsWithTagIds} matching test(s)`}</Typography.Text>
        )}
      </Row>
      <Typography.Text />
      <Form.Item {...tailLayout}>
        <Button
          style={{ marginTop: '5px' }}
          type="primary"
          htmlType="submit"
          loading={isSubmitting}
        >
          Save
        </Button>
      </Form.Item>
    </Form>
  )
}
ScrapingTagGroupEditorForm.propTypes = {
  scrapingTagGroup: PropTypes.object,
  tags: PropTypes.array.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
}

ScrapingTagGroupEditorForm.defaultProps = {
  scrapingTagGroup: undefined,
}

export default ScrapingTagGroupEditorForm
